import type React from 'react';
import { Card, CardContent } from '@/components/ui/card';

export function DetailInfoCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-lg border bg-background/60 p-3 transition-colors hover:bg-muted/50">
      <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <div className="truncate text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

export function DetailMetricCard({
  icon: Icon,
  label,
  value,
  helper,
  tone = 'default',
}: {
  icon?: React.ElementType | undefined;
  label: string;
  value: string;
  helper?: string | undefined;
  tone?: 'default' | 'destructive' | 'warning' | undefined;
}) {
  const toneClass =
    tone === 'destructive'
      ? 'border-destructive/20 bg-destructive/5'
      : tone === 'warning'
        ? 'border-amber-200 bg-amber-50/60 dark:border-amber-900/60 dark:bg-amber-950/20'
        : 'bg-card';

  return (
    <Card className={`py-0 ${toneClass}`}>
      <CardContent className="flex min-h-[132px] flex-col justify-between p-4">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
        </div>
        <div>
          <p className="text-2xl font-semibold tracking-tight tabular-nums text-foreground">
            {value}
          </p>
          {helper && <p className="mt-1 text-xs text-muted-foreground">{helper}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function DetailSectionHeader({
  icon: Icon,
  title,
  meta,
}: {
  icon: React.ElementType;
  title: string;
  meta?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
      </div>
      {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
    </div>
  );
}
